import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { externalLinkProps } from '../utils/url';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import {
  Flame, RefreshCw, Sparkles, Youtube, Newspaper, Instagram, Music2,
  ExternalLink, Clock, Loader2, Play, Heart, MessageCircle
} from 'lucide-react';

const SECTIONS = [
  { key: 'viral',   label: 'Viral Trends', icon: Sparkles,  color: 'amber' },
  { key: 'ig',      label: 'Trending IG',  icon: Instagram, color: 'pink'  },
  { key: 'youtube', label: 'Trending YT',  icon: Youtube,   color: 'rose'  },
  { key: 'audio',   label: 'Trending Audio', icon: Music2,  color: 'violet'},
  { key: 'news',    label: 'India News',   icon: Newspaper, color: 'cyan'  },
];

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtNum(n) {
  if (!n && n !== 0) return '';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

export default function TrendingPulse() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('viral');

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/trending-content/latest`, { withCredentials: true });
      setSnapshot(data);
    } catch {
      toast.error('Failed to load trending content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !snapshot) fetchLatest();
  }, [open, snapshot, fetchLatest]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/trending-content/refresh`, {}, { withCredentials: true });
      toast.info('Rebuilding trending content — this takes 2-4 min');
      // Poll /latest every 20s for up to 5 min
      const startedAt = snapshot?.generated_at;
      const deadline = Date.now() + 5 * 60 * 1000;
      const poll = async () => {
        if (Date.now() > deadline) {
          toast.warning('Rebuild still in progress — check again in a minute');
          setRefreshing(false);
          return;
        }
        try {
          const { data } = await axios.get(`${API}/trending-content/latest`, { withCredentials: true });
          if (data?.generated_at && data.generated_at !== startedAt) {
            setSnapshot(data);
            toast.success('Trending content refreshed');
            setRefreshing(false);
            return;
          }
        } catch { /* ignore */ }
        setTimeout(poll, 20000);
      };
      setTimeout(poll, 30000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
      setRefreshing(false);
    }
  };

  const counts = snapshot
    ? SECTIONS.reduce((acc, s) => { acc[s.key] = snapshot[s.key]?.items?.length || 0; return acc; }, {})
    : {};

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="trending-pulse-trigger"
        className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-gradient-to-r from-amber-500/15 to-pink-500/15 hover:from-amber-500/25 hover:to-pink-500/25 border border-amber-400/30 text-amber-300 hover:text-white transition-all group"
      >
        <Flame size={14} className="group-hover:scale-110 transition-transform" />
        <span className="text-[11px] font-semibold tracking-wide uppercase">Trending</span>
        <span className="hidden sm:flex items-center gap-1 ml-0.5 text-[10px] opacity-70">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-2xl bg-[#08080d] border-l border-white/10 overflow-y-auto p-0">
          <SheetHeader className="sticky top-0 z-10 bg-gradient-to-b from-[#08080d] via-[#08080d] to-[#08080d]/90 backdrop-blur-xl border-b border-white/5 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center">
                  <Flame size={18} className="text-white" />
                </div>
                <div>
                  <SheetTitle className="text-white font-heading text-lg">Trending Pulse · India</SheetTitle>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <Clock size={10} />
                    {snapshot ? `${snapshot.slot || ''} · updated ${timeAgo(snapshot.generated_at)}` : '—'}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <Button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  variant="outline"
                  className="bg-white/5 border-white/10 text-slate-300 hover:text-white h-8 text-[11px]"
                  data-testid="trending-refresh-btn"
                >
                  {refreshing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                  {refreshing ? 'Refreshing (1-3 min)…' : 'Refresh'}
                </Button>
              )}
            </div>

            <div className="flex gap-1 mt-4 overflow-x-auto">
              {SECTIONS.map(s => {
                const Icon = s.icon;
                const active = activeTab === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveTab(s.key)}
                    data-testid={`trending-tab-${s.key}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold tracking-wide uppercase whitespace-nowrap transition-colors ${
                      active
                        ? `bg-${s.color}-500/15 text-${s.color}-300 border border-${s.color}-400/30`
                        : 'bg-white/5 text-slate-500 border border-white/5 hover:text-slate-300'
                    }`}
                  >
                    <Icon size={12} />
                    {s.label}
                    {counts[s.key] > 0 && (
                      <span className={`ml-0.5 px-1.5 rounded-full text-[9px] ${active ? `bg-${s.color}-500/30` : 'bg-white/10'}`}>
                        {counts[s.key]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </SheetHeader>

          <div className="px-6 py-5" data-testid="trending-content-body">
            {loading && !snapshot && (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading trending content…
              </div>
            )}

            {snapshot && activeTab === 'viral'   && (<><StaleBanner on={snapshot.viral?.stale} /><ViralList   items={snapshot.viral?.items   || []} err={snapshot.viral?.error}   /></>)}
            {snapshot && activeTab === 'ig'      && (<><StaleBanner on={snapshot.ig?.stale}    /><IGList      items={snapshot.ig?.items      || []} err={snapshot.ig?.error}      /></>)}
            {snapshot && activeTab === 'youtube' && (<><StaleBanner on={snapshot.youtube?.stale}/><YoutubeList items={snapshot.youtube?.items || []} err={snapshot.youtube?.error} /></>)}
            {snapshot && activeTab === 'audio'   && (<><StaleBanner on={snapshot.audio?.stale}  /><AudioList   items={snapshot.audio?.items   || []} err={snapshot.audio?.error}   /></>)}
            {snapshot && activeTab === 'news'    && (<><StaleBanner on={snapshot.news?.stale}   /><NewsList    items={snapshot.news?.items    || []} err={snapshot.news?.error}    /></>)}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function EmptyErr({ err }) {
  let msg = 'Nothing trending here right now.';
  if (err) {
    const e = String(err);
    if (e.includes('429') || e.includes('RESOURCE_EXHAUSTED') || e.toLowerCase().includes('quota')) {
      msg = 'AI quota hit — refreshing tomorrow morning';
    } else if (e.includes('503') || e.includes('UNAVAILABLE')) {
      msg = 'Source temporarily unavailable — try refresh in a few minutes';
    } else {
      msg = 'Not available right now';
    }
  }
  return <div className="text-center py-12 text-slate-500 text-sm">{msg}</div>;
}

function StaleBanner({ on }) {
  if (!on) return null;
  return (
    <div className="mb-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-300 flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      Showing last good data — fresh fetch will retry on the next cycle.
    </div>
  );
}

// ---------- Viral Trends (Gemini, no per-item link) ----------
function ViralList({ items, err }) {
  if (!items.length) return <EmptyErr err={err} />;
  return (
    <div className="space-y-3">
      {items.map((v, i) => (
        <div key={i} className="glass-card p-4 hover:border-amber-500/30 transition-colors" data-testid={`viral-item-${i}`}>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 font-bold text-xs flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="text-sm font-semibold text-white">{v.title}</h4>
                {v.type && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">{v.type}</span>}
                {v.stay_away && <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">⚠ Brand-risky</span>}
              </div>
              {v.origin && <p className="text-[11px] text-slate-400 mb-1"><span className="text-slate-500 font-semibold">Origin: </span>{v.origin}</p>}
              {v.adoption && <p className="text-[11px] text-slate-400"><span className="text-slate-500 font-semibold">Adoption: </span>{v.adoption}</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Trending IG (Apify, with inline thumbnail + Open Reel link) ----------
function IGList({ items, err }) {
  if (!items.length) return <EmptyErr err={err} />;
  return (
    <div className="space-y-3">
      {items.map((r, i) => (
        <a
          key={i}
          {...externalLinkProps(r.url)}
          className="group flex items-stretch gap-3 p-3 bg-white/[0.02] hover:bg-pink-500/5 border border-white/5 hover:border-pink-500/30 rounded-lg transition-colors"
          data-testid={`ig-item-${i}`}
        >
          <div className="relative w-20 h-28 rounded-md overflow-hidden flex-shrink-0 bg-gradient-to-br from-pink-500/20 via-fuchsia-500/15 to-purple-500/20 border border-pink-500/20 flex items-center justify-center">
            <Instagram size={22} className="text-pink-300/70" />
            <div className="absolute inset-x-0 bottom-0 bg-black/60 backdrop-blur-sm px-1.5 py-1 flex items-center justify-center">
              <Play size={10} className="text-white" fill="white" />
              <span className="text-[9px] text-white font-semibold ml-1">{fmtNum(r.plays)}</span>
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="h-5 w-5 rounded-md bg-pink-500/15 border border-pink-500/30 flex items-center justify-center text-pink-300 font-bold text-[10px]">
                {i + 1}
              </span>
              <span className="text-xs font-semibold text-white group-hover:text-pink-300 truncate">@{r.owner}</span>
              <ExternalLink size={10} className="text-slate-600 group-hover:text-pink-400" />
            </div>
            {r.caption && <p className="text-[11px] text-slate-400 line-clamp-2 mb-1.5">{r.caption}</p>}
            <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-auto">
              <span className="flex items-center gap-1"><Play size={9} />{fmtNum(r.plays)}</span>
              <span className="flex items-center gap-1"><Heart size={9} />{fmtNum(r.likes)}</span>
              <span className="flex items-center gap-1"><MessageCircle size={9} />{fmtNum(r.comments)}</span>
              {r.audio_title && !r.audio_original && (
                <span className="flex items-center gap-1 text-violet-400 truncate max-w-[180px]">
                  <Music2 size={9} />{r.audio_title}
                </span>
              )}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ---------- Trending YT (Gemini) ----------
function YoutubeList({ items, err }) {
  if (!items.length) return <EmptyErr err={err} />;
  return (
    <div className="space-y-3">
      {items.map((y, i) => (
        <div key={i} className="glass-card p-4 hover:border-rose-500/30 transition-colors" data-testid={`youtube-item-${i}`}>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-md bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-300 font-bold text-xs flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-white mb-1">{y.title}</h4>
              {y.channel && (
                <p className="text-[11px] text-slate-400 mb-1">
                  by <a {...externalLinkProps(`https://www.youtube.com/results?search_query=${encodeURIComponent(y.channel)}`)} className="text-rose-300 hover:underline">{y.channel}</a>
                  <a {...externalLinkProps(`https://www.youtube.com/results?search_query=${encodeURIComponent(y.title)}`)} className="ml-2 text-[10px] text-slate-500 hover:text-rose-300 inline-flex items-center gap-0.5">
                    <ExternalLink size={9} /> search
                  </a>
                </p>
              )}
              {y.why_watch && <p className="text-[11px] text-slate-400 leading-relaxed mb-2">{y.why_watch}</p>}
              {y.category && <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/20">{y.category}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Trending Audio (Apify aggregated) ----------
function AudioList({ items, err }) {
  if (!items.length) return <EmptyErr err={err} />;
  return (
    <div className="space-y-3">
      {items.map((a, i) => {
        const sample = a.sample_reels?.[0];
        return (
          <div key={i} className="glass-card p-4 hover:border-violet-500/30 transition-colors" data-testid={`audio-item-${i}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500/30 to-pink-500/30 border border-violet-500/40 flex items-center justify-center flex-shrink-0">
                <Music2 size={16} className="text-violet-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-semibold text-white truncate" title={a.song}>{a.song}</h4>
                    {a.artist && <p className="text-[11px] text-violet-300 truncate">{a.artist}</p>}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-violet-500/15 text-violet-200 border border-violet-500/30 font-bold flex-shrink-0">
                    {a.reel_count}× used
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><Play size={9} />{fmtNum(a.total_plays)} total plays</span>
                  <span className="flex items-center gap-1"><Heart size={9} />{fmtNum(a.total_likes)} total likes</span>
                </div>
                {a.sample_reels?.length > 0 && (
                  <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                    <span className="text-[10px] text-slate-500">Sample reels:</span>
                    {a.sample_reels.map((s, j) => (
                      <a
                        key={j}
                        {...externalLinkProps(s.url)}
                        className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5"
                        data-testid={`audio-${i}-sample-${j}`}
                      >
                        <ExternalLink size={9} /> @{s.owner}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- India News (NewsAPI) ----------
function NewsList({ items, err }) {
  if (!items.length) return <EmptyErr err={err} />;
  return (
    <div className="space-y-2">
      {items.map((n, i) => (
        <a
          key={i}
          {...externalLinkProps(n.url)}
          className="group flex items-start gap-3 p-3 bg-white/[0.02] hover:bg-cyan-500/5 border border-white/5 hover:border-cyan-500/20 rounded-lg transition-colors"
          data-testid={`news-item-${i}`}
        >
          {n.image && (
            <img src={n.image} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0 bg-white/5" onError={e => e.target.style.display = 'none'} />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-white group-hover:text-cyan-300 line-clamp-2 mb-1">{n.title}</h4>
            {n.description && <p className="text-[11px] text-slate-500 line-clamp-2 mb-1">{n.description}</p>}
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="text-cyan-400/80 font-medium">{n.source}</span>
              <span>·</span>
              <span>{timeAgo(n.published_at)}</span>
              <ExternalLink size={9} className="ml-auto text-slate-600 group-hover:text-cyan-400" />
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}
